// ABOUTME: Sidebar status cards showing current time, refresh times, and last transaction.
// ABOUTME: Displayed in the sidebar with left-border card design matching the mockup.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button } from '@mui/material';
import {
  ClockIcon,
  ArrowPathIcon,
  CircleStackIcon,
  BrainIcon,
  BanknoteIcon,
  Cog6ToothIcon,
} from './icons/HeroIcons';
import { colors } from '../theme/colors';
import { format, parseISO, isToday } from 'date-fns';

function StatusBar({ currentTime, dashboardData, onReloadConfig }) {
  const { t } = useTranslation();

  const formatTimeWithWarning = (dateTimeString) => {
    if (!dateTimeString) return { time: 'N/A', date: null, warning: true };

    try {
      const date = parseISO(dateTimeString);
      const today = isToday(date);

      return {
        time: format(date, 'HH:mm:ss'),
        date: format(date, 'yyyy-MM-dd'),
        warning: !today,
      };
    } catch (e) {
      return { time: 'Invalid', date: null, warning: true };
    }
  };

  // Current time card - simple design
  const CurrentTimeCard = ({ icon: Icon, label, time }) => (
    <Box
      sx={{
        bgcolor: 'white',
        borderRadius: '10px',
        border: `1px solid ${colors.border.light}`,
        p: 1.5,
        mb: 1,
      }}
    >
      {/* Icon + Label row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Icon width={28} height={28} style={{ color: colors.ui.button }} />
        <Box
          sx={{
            fontSize: '0.875rem',
            fontWeight: 700,
            color: '#525252',
          }}
        >
          {label}
        </Box>
      </Box>

      {/* Large time */}
      <Box sx={{ mt: 1 }}>
        <Box
          sx={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#1f2937',
            lineHeight: 1.2,
          }}
        >
          {time}
        </Box>
      </Box>
    </Box>
  );

  // Refresh status card - normal (today) vs warning (stale) design
  const RefreshCard = ({ icon: Icon, label, time, date, warning }) => (
    <Box
      sx={{
        bgcolor: 'white',
        borderRadius: '10px',
        border: warning ? `2px solid ${colors.status.warning}` : `1px solid ${colors.border.light}`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        p: 1.5,
        mb: 1,
      }}
    >
      {/* Icon + Label row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Icon width={28} height={28} style={{ color: warning ? colors.status.warning : colors.ui.button }} />
        <Box
          sx={{
            fontSize: '0.875rem',
            fontWeight: 700,
            color: warning ? colors.status.warning : '#525252',
          }}
        >
          {label}
        </Box>
      </Box>

      {/* Refresh info group */}
      <Box sx={{ mt: 1 }}>
        {warning ? (
          <>
            {/* Warning mode: time small on top, date large below */}
            <Box
              sx={{
                fontSize: '0.8rem',
                color: colors.status.warning,
              }}
            >
              {time}
            </Box>
            <Box
              sx={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: colors.status.warning,
                lineHeight: 1.2,
              }}
            >
              {date}
            </Box>
          </>
        ) : (
          /* Normal mode: just show time large (same size as CurrentTimeCard) */
          <Box
            sx={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#1f2937',
              lineHeight: 1.2,
            }}
          >
            {time}
          </Box>
        )}
      </Box>
    </Box>
  );

  const dashboardRefresh = formatTimeWithWarning(dashboardData?.last_update);
  const databaseRefresh = formatTimeWithWarning(dashboardData?.last_data_refresh);
  const forecastUpdate = formatTimeWithWarning(dashboardData?.last_forecast_update);
  const lastTransaction = formatTimeWithWarning(dashboardData?.last_transaction_time);

  return (
    <Box>
      {/* Current Time */}
      <CurrentTimeCard
        icon={ClockIcon}
        label={t('dashboard.currentTime')}
        time={format(currentTime, 'HH:mm:ss')}
      />

      {/* Dashboard Refresh */}
      <RefreshCard
        icon={ArrowPathIcon}
        label={t('dashboard.dashboardRefresh')}
        time={dashboardRefresh.time}
        date={dashboardRefresh.date}
        warning={dashboardRefresh.warning}
      />

      {/* Database Refresh */}
      <RefreshCard
        icon={CircleStackIcon}
        label={t('dashboard.databaseRefresh')}
        time={databaseRefresh.time}
        date={databaseRefresh.date}
        warning={databaseRefresh.warning}
      />

      {/* Forecast Update */}
      <RefreshCard
        icon={BrainIcon}
        label={t('dashboard.forecastUpdate')}
        time={forecastUpdate.time}
        date={forecastUpdate.date}
        warning={forecastUpdate.warning}
      />

      {/* Last Transaction */}
      <RefreshCard
        icon={BanknoteIcon}
        label={t('dashboard.lastTransaction')}
        time={lastTransaction.time}
        date={lastTransaction.date}
        warning={lastTransaction.warning}
      />

      {/* Configuration Button */}
      <Button
        onClick={() => { window.location.href = '/config'; }}
        startIcon={<Cog6ToothIcon width={20} height={20} style={{ color: 'white' }} />}
        fullWidth
        sx={{
          bgcolor: colors.ui.button,
          color: 'white',
          textTransform: 'none',
          borderRadius: '8px',
          py: 1,
          fontWeight: 500,
          fontSize: '0.8rem',
          '&:hover': { bgcolor: colors.ui.buttonHover },
        }}
      >
        {t('config.title')}
      </Button>
    </Box>
  );
}

export default StatusBar;
